import {
  List,
  Anchor,
  Image,
  createStyles,
  Group,
  Title,
  Text,
  Container,
  ActionIcon,
  Grid,
  Col,
  Box,
} from '@mantine/core';
import { IconBrandLinkedin } from '@tabler/icons-react';
import { ourStoryDE, ourStoryEN, ourStoryNL } from './languages/ourStory';
import React from 'react';
import Anja from '../../assets/team/Anja_profile.jpeg';

const useStyles = createStyles((theme) => ({
  section: {
    marginBottom: theme.spacing.xl,
  },
  sectionTitle: {
    marginBottom: theme.spacing.md,
    fontSize: '25px',
    color: 'Gray/900',
    fontWeight: 600,
    fontFamily: 'Inter, sans-serif',
    [theme.fn.smallerThan('sm')]: {
      fontSize: '20px',
    },
  },
  sectionText: {
    fontSize: '18px',
    color: '#667085',
    fontWeight: 400,
    fontFamily: 'Inter, sans-serif',
    maxWidth: '720px',
    [theme.fn.smallerThan('sm')]: {
      fontSize: '16px',
    },
  },
  sectionImage: {
    marginTop: theme.spacing.md,
    width: '720px',
    height: '300px',
    borderRadius: 24,
    [theme.fn.smallerThan('sm')]: {
      width: '100%',
      height: 200,
    },
  },
  mainTitle: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 600,
    fontSize: '48px',
    color: 'Gray/900',
    [theme.fn.smallerThan('sm')]: {
      fontSize: '30px',
      lineHeight: 1.3,
    },
  },
  authorBioContainer: {
    backgroundColor: '#1D2939',
    borderRadius: '12px',
    overflow: 'hidden',
    padding: 0,
    marginBottom: theme.spacing.md,
  },
  authorContentWrapper: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    [theme.fn.smallerThan('md')]: {
      flexDirection: 'column-reverse',
    },
  },
  authorTextCol: {
    padding: '32px',
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    [theme.fn.smallerThan('md')]: {
      padding: '24px',
    },
  },
  authorImageCol: {
    height: '100%',
    width: '100%',
    [theme.fn.smallerThan('md')]: {
      height: '250px',
    },
  },
  authorImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
}));

function TableOfContents({ sections }) {
  return (
    <List spacing="xs" listStyleType="none" style={{ paddingLeft: 0, paddingInlineStart: '1rem' }}>
      {sections.map((section, index) => (
        <List.Item key={index}>
          <Anchor
            href={`#${section.id}`}
            size="md"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              display: 'block',
              padding: '6px 0',
              color: '#667085',
              textDecoration: 'none',
              '&:hover': {
                color: '#003D51',
              },
            }}
          >
            {section.title}
          </Anchor>
        </List.Item>
      ))}
    </List>
  );
}

function Section({ id, title, content, imageSrc, altText }) {
  const { classes } = useStyles();
  return (
    <div id={id} className={classes.section}>
      <Title order={2} className={classes.sectionTitle}>{title}</Title>
      <Text className={classes.sectionText}>{content}</Text>
      {imageSrc && (
        <Box mt="md">
          <img
            src={imageSrc}
            alt={altText}
            className={classes.sectionImage}
            style={{ maxWidth: '100%', display: 'block' }}
          />
        </Box>
      )}
    </div>
  );
}
export default function OurStory() {
  const { classes } = useStyles();

  const languagePrefix = window.location.pathname.startsWith('/nl')
    ? '/nl'
    : window.location.pathname.startsWith('/de')
    ? '/de'
    : '';

  const sections = languagePrefix === '/nl'
    ? ourStoryNL
    : languagePrefix === '/de'
    ? ourStoryDE
    : ourStoryEN;

  return (
    <>
      <Container pt={100}>
        <Title
          order={4}
          weight={200}
          align="left"
          mb="md"
          style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            fontSize: '16px',
            color: '#003D51',
          }}
        >
          Our Story
        </Title>
        <Title
          order={1}
          align="left"
          mt="md"
          className={classes.mainTitle}
        >
          Our story: From Humble Beginnings to a Sustainable and Innovative Company
        </Title>
        <Text
          align="left"
          mt="md"
          style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '20px',
            color: '#667085',
            maxWidth: '960px',
          }}
        >
          What began as a humble short-term rental operation has since evolved into a company that places great importance on assessing the sustainability and financial health of its own properties. Nowadays, we are focused on developing innovative products that help hosts become equipped for the shared economy of the 21st century.
        </Text>
      </Container>

      <Container pt="md">
        <Grid gutter="xl">
          <Col span={12} md={4}>
            <TableOfContents sections={sections} />
          </Col>
          <Col span={12} md={8}>
            <div>
              {sections.map((section) => (
                <Section
                  key={section.id}
                  id={section.id}
                  title={section.title}
                  content={section.content}
                  imageSrc={section.imageSrc}
                  altText={section.altText}
                />
              ))}
            </div>
          </Col>
        </Grid>
      </Container>

      {/* Author Bio Section */}
      <Container size="lg" className={classes.authorBioContainer}>
        <div className={classes.authorContentWrapper}>
          <div className={classes.authorTextCol}>
            <Group noWrap align="center" mb="md">
              <ActionIcon
                component="a"
                href="https://www.linkedin.com/in/anja-bulteel"
                size={40}
                variant="filled"
                style={{ backgroundColor: '#D0D5DD', color: '#1D2939' }}
              >
                <IconBrandLinkedin size="1.2rem" />
              </ActionIcon>
              <div>
                <Text
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#F9FBF9',
                  }}
                >
                  Anja Bulteel
                </Text>
                <Text
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '10px',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    color: '#F9FBF9',
                  }}
                >
                  Founder
                </Text>
              </div>
            </Group>
            <Text
              style={{
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                color: '#E4E7EC',
                lineHeight: 1.6,
              }}
            >
              Hello readers! My name is Anja, and I'm the author of this blog post. I'm the founder of ResiRent, and I'm passionate about sustainable tourism. I decided to write this post to share my knowledge and insights on sustainable management of a holiday rental, and I hope you found it helpful! In my free time, I enjoy hiking with Jeff (my dog), and I'm always looking for ways to expand my knowledge and skills. Thank you for taking the time to read my post, and I hope you’ll check out more of my content in the future!
            </Text>
          </div>

          <div className={classes.authorImageCol}>
            <img src={Anja} alt="Anja Bulteel" className={classes.authorImage} />
          </div>
        </div>
      </Container>
    </>
  );
}
